import { cn } from "../ui/cn";

/**
 * The three form primitives the CRM's forms are built from.
 *
 * Their own module rather than living in whichever dialog needed them first:
 * they are used by the company dialog, the contact form and the activity
 * composer, and an import of `Field` from `CompanyDialog` reads as a mistake
 * every time somebody opens one of the other two.
 */

export function Field({
  label,
  hint,
  required,
  className,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-faint">
        {label}
        {required ? <span className="text-brand-600"> *</span> : null}
      </span>
      {children}
      {hint ? <span className="text-[11px] text-faint">{hint}</span> : null}
    </label>
  );
}

export function Input({
  value,
  onChange,
  type = "text",
  required,
  autoFocus,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      required={required}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="h-8 rounded-md border border-line bg-app px-2 text-sm text-content outline-none focus:border-brand-400"
    />
  );
}

export function Picker({
  value,
  onChange,
  options,
  placeholder = "Not set",
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        "h-8 rounded-md border border-line bg-app px-2 text-sm outline-none focus:border-brand-400",
        value ? "text-content" : "text-faint",
      )}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
